import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const PersonalityMatrix = () => {
  const [activeCategory, setActiveCategory] = useState('workStyle');

  const personalityData = {
    workStyle: {
      title: 'Work Style Preferences',
      icon: 'Briefcase',
      color: '#00d4ff',
      metrics: [
        { name: 'Independent Work', value: 85, description: 'Thrives in autonomous environments with minimal supervision' },
        { name: 'Team Collaboration', value: 92, description: 'Excels in collaborative settings and cross-functional teams' },
        { name: 'Problem Solving', value: 96, description: 'Approaches complex challenges with analytical thinking' },
        { name: 'Innovation Focus', value: 89, description: 'Constantly seeks new technologies and methodologies' },
        { name: 'Detail Orientation', value: 88, description: 'Maintains high standards for code quality and documentation' },
        { name: 'Adaptability', value: 94, description: 'Quickly adjusts to changing requirements and technologies' }
      ]
    },
    communication: {
      title: 'Communication Approaches',
      icon: 'MessageSquare',
      color: '#00ff88',
      metrics: [
        { name: 'Technical Explanation', value: 95, description: 'Breaks down complex concepts into understandable terms' },
        { name: 'Active Listening', value: 91, description: 'Carefully considers stakeholder needs and feedback' },
        { name: 'Written Communication', value: 87, description: 'Clear and comprehensive documentation practices' },
        { name: 'Presentation Skills', value: 83, description: 'Effective at demonstrating solutions and concepts' },
        { name: 'Cross-Cultural Awareness', value: 90, description: 'Adapts communication style for diverse audiences' },
        { name: 'Conflict Resolution', value: 86, description: 'Mediates technical disagreements constructively' }
      ]
    },
    collaboration: {
      title: 'Collaboration Philosophy',
      icon: 'Users',
      color: '#8844ff',
      metrics: [
        { name: 'Knowledge Sharing', value: 97, description: 'Actively mentors and shares expertise with team members' },
        { name: 'Code Review Quality', value: 93, description: 'Provides constructive and educational feedback' },
        { name: 'Pair Programming', value: 88, description: 'Enjoys collaborative coding sessions and knowledge transfer' },
        { name: 'Open Source Contribution', value: 91, description: 'Regular contributor to community projects' },
        { name: 'Leadership Initiative', value: 85, description: 'Takes ownership of project direction and team guidance' },
        { name: 'Inclusive Practices', value: 94, description: 'Ensures all team members feel valued and heard' }
      ]
    },
    learning: {
      title: 'Learning & Growth',
      icon: 'BookOpen',
      color: '#ffaa00',
      metrics: [
        { name: 'Continuous Learning', value: 98, description: 'Constantly exploring new technologies and methodologies' },
        { name: 'Experimentation', value: 92, description: 'Builds prototypes and proof-of-concepts regularly' },
        { name: 'Industry Awareness', value: 89, description: 'Stays current with technology trends and best practices' },
        { name: 'Skill Diversification', value: 87, description: 'Expands expertise across multiple technology stacks' },
        { name: 'Teaching Ability', value: 95, description: 'Effectively transfers knowledge through mentoring' },
        { name: 'Research Orientation', value: 84, description: 'Investigates emerging technologies and their applications' }
      ]
    }
  };

  const categories = Object.keys(personalityData);

  const getRadarPoints = (metrics) => {
    const angleStep = (2 * Math.PI) / metrics.length;
    return metrics.map((metric, index) => {
      const angle = index * angleStep - Math.PI / 2;
      const radius = (metric.value / 100) * 80;
      const x = 100 + radius * Math.cos(angle);
      const y = 100 + radius * Math.sin(angle);
      return { x, y, angle, radius };
    });
  };

  const currentData = personalityData[activeCategory];
  const radarPoints = getRadarPoints(currentData.metrics);

  return (
    <div className="w-full max-w-6xl mx-auto">
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="font-orbitron font-bold text-3xl text-foreground mb-4">
          Personality Matrix
        </h2>
        <p className="text-text-secondary font-inter max-w-2xl mx-auto">
          A comprehensive analysis of work style preferences, communication approaches, and collaboration philosophies
        </p>
      </motion.div>

      {/* Category Navigation */}
      <div className="flex flex-wrap justify-center gap-4 mb-8">
        {categories.map((category, index) => {
          const data = personalityData[category];
          return (
            <motion.button
              key={category}
              className={`flex items-center space-x-3 px-6 py-3 rounded-xl font-inter font-medium transition-all duration-300 cyber-hover ${
                activeCategory === category
                  ? 'cyber-border-active cyber-glow text-foreground'
                  : 'cyber-border text-text-secondary hover:text-foreground'
              }`}
              onClick={() => setActiveCategory(category)}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                  activeCategory === category ? 'cyber-glow' : ''
                }`}
                style={{
                  backgroundColor: `${data.color}20`,
                  borderColor: activeCategory === category ? data.color : `${data.color}40`
                }}
              >
                <Icon 
                  name={data.icon} 
                  size={16} 
                  style={{ color: data.color }} 
                />
              </div>
              <span>{data.title}</span>
            </motion.button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Radar Chart */}
        <motion.div
          className="glass-morphism rounded-2xl p-8 cyber-border"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-6">
            <div className="flex items-center justify-center space-x-3 mb-2">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center cyber-glow"
                style={{ backgroundColor: `${currentData.color}20` }}
              >
                <Icon name={currentData.icon} size={24} style={{ color: currentData.color }} />
              </div>
              <h3 className="font-orbitron font-bold text-xl text-foreground">
                {currentData.title}
              </h3>
            </div>
          </div>

          <div className="relative w-full max-w-md mx-auto">
            <svg viewBox="0 0 200 200" className="w-full h-auto">
              {/* Background Grid */}
              <defs>
                <radialGradient id="radarGradient" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor={currentData.color} stopOpacity="0.1" />
                  <stop offset="100%" stopColor={currentData.color} stopOpacity="0.05" />
                </radialGradient>
              </defs>

              {/* Concentric Circles */}
              {[20, 40, 60, 80].map((radius, index) => (
                <circle
                  key={radius}
                  cx="100"
                  cy="100"
                  r={radius}
                  fill="none"
                  stroke={currentData.color}
                  strokeWidth="1"
                  opacity={0.2 - index * 0.03}
                />
              ))}

              {/* Axis Lines */}
              {currentData.metrics.map((_, index) => {
                const angle = (index * 2 * Math.PI) / currentData.metrics.length - Math.PI / 2;
                const x2 = 100 + 80 * Math.cos(angle);
                const y2 = 100 + 80 * Math.sin(angle);
                return (
                  <line
                    key={index}
                    x1="100"
                    y1="100"
                    x2={x2}
                    y2={y2}
                    stroke={currentData.color}
                    strokeWidth="1"
                    opacity="0.3"
                  />
                );
              })}

              {/* Data Polygon */}
              <motion.polygon
                points={radarPoints.map(point => `${point.x},${point.y}`).join(' ')}
                fill="url(#radarGradient)"
                stroke={currentData.color}
                strokeWidth="2"
                opacity="0.8"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1, ease: "easeOut" }}
              />

              {/* Data Points */}
              {radarPoints.map((point, index) => (
                <motion.circle
                  key={index}
                  cx={point.x}
                  cy={point.y}
                  r="4"
                  fill={currentData.color}
                  stroke="#ffffff"
                  strokeWidth="2"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="cursor-pointer"
                >
                  <title>{currentData.metrics[index].name}: {currentData.metrics[index].value}%</title>
                </motion.circle>
              ))}

              {/* Labels */}
              {currentData.metrics.map((metric, index) => {
                const angle = (index * 2 * Math.PI) / currentData.metrics.length - Math.PI / 2;
                const labelRadius = 95;
                const x = 100 + labelRadius * Math.cos(angle);
                const y = 100 + labelRadius * Math.sin(angle);
                
                return (
                  <text
                    key={index}
                    x={x}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="text-xs font-mono fill-current text-text-secondary"
                    transform={`rotate(${angle > Math.PI / 2 || angle < -Math.PI / 2 ? angle * 180 / Math.PI + 180 : angle * 180 / Math.PI}, ${x}, ${y})`}
                  >
                    {metric.name}
                  </text>
                );
              })}
            </svg>
          </div>
        </motion.div>

        {/* Metrics List */}
        <motion.div
          className="space-y-4"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {currentData.metrics.map((metric, index) => (
            <motion.div
              key={metric.name}
              className="glass-morphism rounded-xl p-4 cyber-border cyber-hover"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-inter font-semibold text-foreground">
                  {metric.name}
                </h4>
                <span 
                  className="font-mono font-bold text-lg"
                  style={{ color: currentData.color }}
                >
                  {metric.value}%
                </span>
              </div>
              
              <div className="w-full bg-surface rounded-full h-2 mb-3">
                <motion.div
                  className="h-2 rounded-full"
                  style={{ backgroundColor: currentData.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${metric.value}%` }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                />
              </div>
              
              <p className="text-sm text-text-secondary leading-relaxed">
                {metric.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Matrix Summary */}
      <motion.div
        className="mt-12 glass-morphism rounded-2xl p-8 cyber-border"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <div className="text-center mb-6">
          <h3 className="font-orbitron font-bold text-2xl text-foreground mb-2">
            Personality Matrix Summary
          </h3>
          <p className="text-text-secondary font-inter">
            Core traits and characteristics that define the collaborative approach
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((category, index) => {
            const data = personalityData[category];
            const averageScore = Math.round(
              data.metrics.reduce((acc, metric) => acc + metric.value, 0) / data.metrics.length
            );
            
            return (
              <motion.div
                key={category}
                className="text-center p-4 rounded-xl bg-surface/30 border border-border"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 cyber-glow"
                  style={{ backgroundColor: `${data.color}20` }}
                >
                  <Icon name={data.icon} size={24} style={{ color: data.color }} />
                </div>
                <div 
                  className="font-orbitron font-bold text-2xl mb-1"
                  style={{ color: data.color }}
                >
                  {averageScore}%
                </div>
                <div className="text-sm text-text-secondary">
                  {data.title}
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

export default PersonalityMatrix;